import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ClearOutlined,
  EditOutlined,
  FileDownloadDoneOutlined,
  SettingsOutlined
} from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Alert, AlertTitle, alpha, Box, Button, List, ListItem, ListItemText } from '@mui/material';
import isEqual from 'lodash/isEqual';

import AddApp from '@/assets/icons/AddApp';
import CancelablePopup from '@/components/CancelablePopup';
import presets from '@/components/CancelablePopup/presets';
import { PageInformationContext } from '@/context/PageInformationContext';
import { RemoveWidgetContext } from '@/context/RemoveWidgetContext';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import AppsOrder from '@/features/Dashboard/pages/Edit/components/AppsOrder';
import EditDashboardName from '@/features/Dashboard/pages/Edit/components/EditDashboardName';
import { TilesOrder } from '@/features/Dashboard/pages/Edit/components/TilesOrder';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import { useUpdateDashboard } from '@/hooks/api/dashboard';

import { DashboardLayout } from '../../components/DashboardLayout';
import { ButtonsContainer } from '../../drawer/components/ButtonsContainer';
import { MarketApps, MarketAppWithTabs, TileBigNumber } from '../../types';

export const EditDashboard = ({
  onClose,
  dashboardConfig
}: {
  onClose: () => void;
  dashboardConfig: MarketApps;
}) => {
  const [initialMarketTabApps, setInitialMarketTabApps] = useState<MarketAppWithTabs[]>([]);

  const {
    marketTabApps,
    setMarketTabApps,
    tiles,
    setTiles,
    dashboardName,
    initialDashboardName,
    setDashboardName,
    currentTabName
  } = useEditDashboardDraft();

  const { setMode } = useAppForm();

  useEffect(() => {
    setMode('edit');
  }, []);

  const [isDiscardPopupOpen, setIsDiscardPopupOpen] = useState(false);
  const [isRemoveAllPopupOpen, setIsRemoveAllPopupOpen] = useState(false);
  const [isRemoveWidgetPopupOpen, setIsRemoveWidgetPopupOpen] = useState(false);
  const [removeWidgetCallback, setRemoveWidgetCallback] = useState<(() => void) | null>(null);
  const [hasDashboardNameErrors, setHasDashboardNameErrors] = useState(false);

  const { mutate, isSuccess, isPending } = useUpdateDashboard(
    dashboardName,
    'Dashboard updated!',
    currentTabName,
    initialDashboardName
  );

  const [initialTiles, setInitialTiles] = useState<TileBigNumber[]>([]);
  const hasTilesChanges = useMemo(() => {
    return !isEqual(tiles, initialTiles);
  }, [tiles, initialTiles]);

  const hasAppsChanges = useMemo(() => {
    return !isEqual(marketTabApps, initialMarketTabApps);
  }, [marketTabApps, initialMarketTabApps]);

  const hasTabNameErrors = useMemo(() => {
    return marketTabApps.some(app =>
      app.tabs.some(
        tab => !createNameSchema('Name', TABNAME_MAX_LENGTH).safeParse(tab.name ?? '').success
      )
    );
  }, [marketTabApps]);

  const [pendingRemoveAll, setPendingRemoveAll] = useState(false);

  useEffect(() => {
    setMarketTabApps(() => {
      const newMarketTabApps: MarketAppWithTabs[] = [];
      for (const app of dashboardConfig) {
        if ('type' in app && app.type === 'tiles') {
          setInitialTiles(app.tiles);
          setTiles(app.tiles);
        }
        if ('tabs' in app) {
          newMarketTabApps.push(app);
        }
      }
      setInitialMarketTabApps(newMarketTabApps);
      return newMarketTabApps;
    });
  }, [dashboardConfig, setMarketTabApps, setTiles]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  const handleSave = useCallback(() => {
    const payload: MarketApps = [
      ...(tiles.length > 0
        ? [
            {
              type: 'tiles' as const,
              tiles,
              name: 'Tiles',
              metadata: {},
              layout: null,
              id: 'tiles'
            }
          ]
        : []),
      ...marketTabApps
    ];
    mutate(payload);
  }, [marketTabApps, mutate, tiles]);

  useEffect(() => {
    if (pendingRemoveAll) {
      mutate([]);
      setPendingRemoveAll(false);
    }
  }, [mutate, pendingRemoveAll]);

  const handleRemoveAll = useCallback(() => {
    setDashboardName('New Dashboard');
    setPendingRemoveAll(true);
  }, [setDashboardName, setPendingRemoveAll]);

  const openRemoveModal = useCallback((onOk: () => void) => {
    setRemoveWidgetCallback(() => onOk);
    setIsRemoveWidgetPopupOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDashboardName(initialDashboardName);
    onClose();
  }, [onClose, initialDashboardName, setDashboardName]);

  const hasChanges = useMemo(() => {
    return hasTilesChanges || hasAppsChanges || initialDashboardName !== dashboardName;
  }, [hasTilesChanges, hasAppsChanges, dashboardName, initialDashboardName]);

  const handleDiscardClick = useCallback(() => {
    if (hasChanges) {
      setIsDiscardPopupOpen(true);
    } else {
      handleClose();
    }
  }, [hasChanges, handleClose]);

  const removeWidgetValue = useMemo(() => {
    return {
      openRemoveModal
    };
  }, [openRemoveModal]);
  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();

  return (
    <RemoveWidgetContext.Provider value={removeWidgetValue}>
      <CancelablePopup
        isOpened={isDiscardPopupOpen}
        setIsOpened={setIsDiscardPopupOpen}
        onConfirm={handleClose}
        {...presets.discard}
      />
      <CancelablePopup
        isOpened={isRemoveWidgetPopupOpen}
        setIsOpened={setIsRemoveWidgetPopupOpen}
        onConfirm={() => {
          if (removeWidgetCallback) {
            removeWidgetCallback();
            setRemoveWidgetCallback(null);
          }
        }}
        {...presets.removeWidget}
      />
      <CancelablePopup
        isOpened={isRemoveAllPopupOpen}
        setIsOpened={setIsRemoveAllPopupOpen}
        onConfirm={handleRemoveAll}
        {...presets.removeAll}
      />
      <DashboardLayout
        isPending={isPending}
        renderRight={
          <ButtonsContainer>
            <Button
              color='secondary'
              disabled={isPending}
              size='large'
              sx={{
                height: '48px',
                width: '192px'
              }}
              variant='contained'
              onClick={handleDiscardClick}
            >
              Discard
            </Button>
            <Button
              color='secondary'
              disabled={isPending}
              size='large'
              sx={{
                height: '48px',
                width: '192px'
              }}
              variant='contained'
              onClick={() => {
                setIsRemoveAllPopupOpen(true);
              }}
            >
              <DeleteOutlineIcon
                sx={{
                  fontSize: '24px !important'
                }}
              />
              Remove all
            </Button>
            <Button
              color='secondary'
              size='large'
              sx={{
                height: '48px',
                width: '192px'
              }}
              variant='contained'
              onClick={() => {
                setIsEditMode(true);
                setEditingTarget(null);
                setDrawerStep(DrawerStep.SelectApp);
              }}
            >
              <AddApp
                fill='#F8F8F8'
                sx={{
                  width: '24px',
                  height: '24px'
                }}
              />
              <Box
                sx={{
                  marginLeft: '8px'
                }}
              >
                Add an app
              </Box>
            </Button>
            <Button
              disabled={isPending || !hasChanges || hasDashboardNameErrors || hasTabNameErrors}
              size='large'
              sx={{
                height: '48px',
                width: '192px'
              }}
              variant='contained'
              onClick={handleSave}
            >
              <FileDownloadDoneOutlined
                sx={{
                  fontSize: '24px !important'
                }}
              />
              Save
            </Button>
          </ButtonsContainer>
        }
        title={<EditDashboardName onValidationChange={setHasDashboardNameErrors} />}
      >
        <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert
              icon={<EditOutlined />}
              severity='error'
              sx={{
                backgroundColor: alpha('#E84E0F', 0.11),

                color: 'primary.main',
                '& .MuiAlert-icon': {
                  color: 'primary.main'
                },
                fontSize: '12px'
              }}
            >
              <AlertTitle
                sx={{
                  fontSize: '12px',
                  mt: 0.5
                }}
              >
                Edit Mode
              </AlertTitle>
              <List
                dense
                sx={{
                  listStyleType: 'square',
                  pl: 1,
                  pt: 1,
                  pb: 0,
                  '& .MuiListItemText-primary': {
                    fontSize: '12px'
                  },
                  '& .MuiListItem-root': {
                    py: 0
                  }
                }}
              >
                <ListItem disableGutters>
                  <Box
                    sx={{ width: '3px', height: '3px', backgroundColor: 'primary.main', mr: 0.5 }}
                  />
                  <ListItemText primary='Drag widgets/tabs to reorder' />
                </ListItem>
                <ListItem disableGutters>
                  <Box
                    sx={{ width: '3px', height: '3px', backgroundColor: 'primary.main', mr: 0.5 }}
                  />
                  <ListItemText
                    primary={
                      <>
                        Use "{' '}
                        <SettingsOutlined
                          sx={{
                            fontSize: '8px'
                          }}
                        />{' '}
                        " icon for settings, "
                        <ClearOutlined
                          sx={{
                            fontSize: '8px'
                          }}
                        />
                        " to remove, "+" to add a new tab
                      </>
                    }
                    sx={{ fontSize: '12px' }}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <Box
                    sx={{ width: '3px', height: '3px', backgroundColor: 'primary.main', mr: 0.5 }}
                  />
                  <ListItemText primary='Click "Save" when done' />
                </ListItem>
              </List>
            </Alert>

            <TilesOrder />
            <AppsOrder />
          </Box>
        </PageInformationContext>
      </DashboardLayout>
    </RemoveWidgetContext.Provider>
  );
};
